import { useState, useRef, useEffect, useCallback } from "react";
import GlobalStyles from "../components/GlobalStyles";
import { TutorialTooltip } from "../components/Section";
import { WITNESS_TYPES, PERSONAS, OBJECTION_TYPES, TIPS, TUTORIAL_STEPS } from "../constants";
import { callClaude, callClaudeStreaming } from "../api";
import { speakText, stopSpeech } from "../speech";
import { useMicrophone, isTurnEnd } from "../hooks/useMicrophone";
import { useRealtimeWitness } from "../hooks/useRealtimeWitness";
import { stripMd, generateReportHTML, openDoc, normalizeTranscript } from "../utils";
import {
  buildSecretaryOpeningPrompt, buildJudgeWitnessOpeningPrompt,
  buildJudgeAfterOathPrompt,
  buildJudgeTransitionPrompt, buildJudgeNextWitnessPrompt,
  buildJudgeClosingPrompt, buildJudgeRulingPrompt,
  buildJudgeWarningSabotagePrompt, buildJudgeTerminateSabotagePrompt,
  buildWitnessPrompt, buildAdversaryPrompt,
  buildAILawyerQuestionPrompt, buildReportPrompt,
  buildWitnessOathResponsePrompt, buildAILawyerClosingPrompt,
} from "../prompts";

// ── Convierte *cursiva* y **negrita** del contenido a HTML seguro ──────────
function renderMsgContent(text) {
  if (!text) return "";
  // Escapar HTML primero para evitar inyección
  let safe = String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  // **negrita** → <strong>
  safe = safe.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  // *cursiva* → <em>
  safe = safe.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  return safe;
}

const TRIAL_CSS = `
.litis-trial {
  --bg:       #ECEAE3;
  --bg2:      #E3E0D8;
  --bg3:      #D6D2C8;
  --white:    #F9F8F5;
  --dark:     #1A170F;
  --text:     #2C2820;
  --muted:    #7A7268;
  --light:    #B0AA9E;
  --accent:   #C4572A;
  --accent2:  #A8451C;
  --green:    #70A33C;
  --blue:     #4A7FB5;
  --border:   rgba(30,26,20,0.10);
  --border2:  rgba(30,26,20,0.07);
  --fis-bg:   rgba(74,127,181,0.12);
  --fis-color:#4A7FB5;
  --def-bg:   rgba(196,87,42,0.12);
  --def-color:#C4572A;
  --r: 10px;
  --sans: 'Fira Sans', system-ui, sans-serif;
  position: fixed; inset: 0;
  display: flex; flex-direction: column;
  background: var(--bg);
  font-family: var(--sans);
  color: var(--text);
  overflow: hidden;
  -webkit-font-smoothing: antialiased;
}
.litis-trial *, .litis-trial *::before, .litis-trial *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* ── TOPBAR ── */
.litis-trial .trial-topbar {
  background: var(--dark); height: 52px;
  display: flex; align-items: center; padding: 0 16px;
  border-bottom: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;
}
.litis-trial .topbar-brand {
  display: flex; align-items: center; padding-right: 16px;
  border-right: 1px solid rgba(255,255,255,0.08); flex-shrink: 0;
  background: none; border-top: none; border-left: none; border-bottom: none; cursor: pointer;
}
.litis-trial .topbar-brand img { height: 20px; width: auto; display: block; }
.litis-trial .topbar-case { padding: 0 16px; flex: 1; min-width: 0; border-right: 1px solid rgba(255,255,255,0.08); }
.litis-trial .topbar-case-title { font-size: 13px; font-weight: 500; color: rgba(255,255,255,0.75); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.litis-trial .topbar-case-sub { font-size: 11px; color: rgba(255,255,255,0.3); }
.litis-trial .topbar-center { display: flex; align-items: center; gap: 10px; padding: 0 16px; border-right: 1px solid rgba(255,255,255,0.08); flex-shrink: 0; }
.litis-trial .topbar-badge { font-size: 10px; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; padding: 4px 10px; border-radius: 5px; white-space: nowrap; }
.litis-trial .badge-int { background: rgba(74,127,181,0.25); color: #8FB4DC; }
.litis-trial .badge-contra { background: rgba(196,87,42,0.25); color: #E08060; }
.litis-trial .badge-cerrado { background: rgba(91,91,168,0.25); color: #A0A0D0; }
.litis-trial .topbar-progress { font-size: 12px; color: rgba(255,255,255,0.4); font-weight: 500; }
.litis-trial .topbar-witness { font-size: 12px; color: rgba(255,255,255,0.65); font-weight: 500; white-space: nowrap; }
.litis-trial .topbar-rt { font-size: 10px; font-weight: 700; letter-spacing: 0.06em; color: #8FB4DC; background: rgba(74,127,181,0.2); padding: 3px 8px; border-radius: 4px; }
.litis-trial .topbar-timer { font-size: 19px; font-weight: 500; color: white; padding: 0 16px; letter-spacing: 0.05em; border-right: 1px solid rgba(255,255,255,0.08); flex-shrink: 0; min-width: 76px; text-align: center; font-variant-numeric: tabular-nums; }
.litis-trial .topbar-actions { display: flex; align-items: center; gap: 7px; padding-left: 14px; flex-shrink: 0; flex-wrap: nowrap; }
.litis-trial .tbtn { font-family: var(--sans); font-size: 12px; font-weight: 500; padding: 6px 13px; border-radius: 6px; cursor: pointer; border: none; transition: all 0.2s; white-space: nowrap; }
.litis-trial .tbtn-ghost { background: rgba(255,255,255,0.07); color: rgba(255,255,255,0.55); }
.litis-trial .tbtn-ghost:hover { background: rgba(255,255,255,0.12); color: white; }
.litis-trial .tbtn-solid { background: rgba(255,255,255,0.12); color: white; border: 1px solid rgba(255,255,255,0.15); }
.litis-trial .tbtn-solid:hover { background: rgba(255,255,255,0.2); }
.litis-trial .tbtn-danger { background: rgba(196,87,42,0.2); color: #E08060; }
.litis-trial .tbtn-danger:hover { background: rgba(196,87,42,0.35); }
.litis-trial .tbtn-accent { background: var(--accent); color: white; }
.litis-trial .tbtn-accent:hover { background: var(--accent2); }

/* ── PAUSA ── */
.litis-trial .trial-pause-overlay {
  position: fixed; inset: 0; z-index: 300;
  background: rgba(15,12,8,0.85); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 18px;
}
.litis-trial .pause-label { font-size: 12px; font-weight: 700; letter-spacing: 0.3em; text-transform: uppercase; color: var(--accent); }
.litis-trial .pause-logo { height: 44px; width: auto; }
.litis-trial .pause-text { font-size: 13px; color: #A89880; font-weight: 500; max-width: 280px; text-align: center; line-height: 1.6; }
.litis-trial .pause-btn { margin-top: 6px; padding: 12px 34px; border-radius: 9px; background: var(--accent); color: white; font-size: 15px; font-family: var(--sans); font-weight: 500; border: none; cursor: pointer; transition: all 0.2s; }
.litis-trial .pause-btn:hover { background: var(--accent2); transform: translateY(-1px); }

/* ── BODY ── */
.litis-trial .trial-body { flex: 1; display: grid; grid-template-columns: 232px 1fr; min-height: 0; overflow: hidden; }

/* ── SIDEBAR ── */
.litis-trial .trial-sidebar { background: var(--white); border-right: 1px solid var(--border); display: flex; flex-direction: column; overflow-y: auto; overflow-x: hidden; }
.litis-trial .sidebar-section { padding: 16px 14px; border-bottom: 1px solid var(--border); flex-shrink: 0; }
.litis-trial .sidebar-section:last-child { border-bottom: none; }
.litis-trial .sidebar-section-label { font-size: 9px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--light); margin-bottom: 12px; }
.litis-trial .active-witness-card { background: var(--bg); border: 1px solid var(--border); border-radius: var(--r); padding: 12px; }
.litis-trial .aw-name { font-size: 14px; font-weight: 500; color: var(--dark); margin-bottom: 2px; }
.litis-trial .aw-role { font-size: 12px; color: var(--muted); margin-bottom: 10px; }
.litis-trial .aw-tag { display: inline-block; font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; padding: 2px 8px; border-radius: 4px; }
.litis-trial .tag-green { background: rgba(112,163,60,0.15); color: var(--green); }
.litis-trial .tag-orange { background: rgba(196,87,42,0.12); color: var(--accent); }
.litis-trial .tag-blue { background: rgba(74,127,181,0.12); color: var(--blue); }
.litis-trial .witness-list { display: flex; flex-direction: column; gap: 4px; }
.litis-trial .wl-item { display: flex; align-items: center; gap: 8px; padding: 7px 8px; border-radius: 7px; transition: background 0.15s; }
.litis-trial .wl-item.active { background: var(--bg2); }
.litis-trial .wl-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.litis-trial .wl-name { font-size: 13px; font-weight: 500; color: var(--text); }
.litis-trial .wl-role { font-size: 11px; color: var(--light); }
.litis-trial .doc-list { display: flex; flex-direction: column; gap: 6px; }
.litis-trial .doc-item { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: 8px; border: 1px solid var(--border); background: var(--bg); cursor: pointer; transition: border-color 0.15s; width: 100%; text-align: left; font-family: var(--sans); }
.litis-trial .doc-item:hover { border-color: rgba(30,26,20,0.2); }
.litis-trial .doc-item:disabled { opacity: 0.6; cursor: default; }
.litis-trial .doc-icon { width: 28px; height: 28px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 13px; flex-shrink: 0; }
.litis-trial .doc-icon-blue { background: rgba(74,127,181,0.12); }
.litis-trial .doc-icon-green { background: rgba(112,163,60,0.12); }
.litis-trial .doc-item-name { font-size: 12px; font-weight: 500; color: var(--text); }
.litis-trial .doc-item-sub { font-size: 11px; color: var(--muted); }
.litis-trial .doc-open { margin-left: auto; font-size: 10px; font-weight: 600; color: var(--light); letter-spacing: 0.04em; flex-shrink: 0; }
.litis-trial .doc-spinner { width: 13px; height: 13px; border: 2px solid var(--border); border-top-color: var(--accent); border-radius: 50%; display: inline-block; animation: litis-spin 0.8s linear infinite; }
.litis-trial .theory-mini { border-radius: 8px; padding: 10px 12px; border: 1px solid var(--border); margin-bottom: 8px; background: var(--bg); }
.litis-trial .theory-mini:last-child { margin-bottom: 0; }
.litis-trial .theory-mini-label { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 5px; }
.litis-trial .theory-mini-text { font-size: 12px; color: var(--muted); line-height: 1.5; }
.litis-trial .session-stat { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px; }
.litis-trial .session-stat:last-child { margin-bottom: 0; }
.litis-trial .session-stat-label { font-size: 12px; color: var(--muted); font-weight: 500; }
.litis-trial .session-stat-val { font-size: 18px; font-weight: 500; color: var(--dark); }

/* ── MAIN ── */
.litis-trial .trial-main { display: flex; flex-direction: column; background: var(--bg); min-width: 0; overflow: hidden; }

.litis-trial .trial-ruling-bar { background: rgba(196,87,42,0.08); border-bottom: 1px solid rgba(196,87,42,0.2); padding: 9px 20px; display: flex; align-items: flex-start; gap: 8px; flex-shrink: 0; animation: litis-slidedown 0.3s ease; }
.litis-trial .trial-ruling-text { font-size: 13px; color: var(--accent2); font-weight: 500; font-style: italic; }
@keyframes litis-slidedown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: none; } }

.litis-trial .trial-ai-bar { background: rgba(196,87,42,0.1); border-bottom: 1px solid rgba(196,87,42,0.2); padding: 8px 20px; display: flex; align-items: center; gap: 8px; flex-shrink: 0; animation: litis-pulse 2s ease-in-out infinite; }
.litis-trial .trial-ai-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--accent); flex-shrink: 0; }
.litis-trial .trial-ai-bar span { font-size: 12px; color: var(--accent2); font-weight: 700; letter-spacing: 0.04em; }
@keyframes litis-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.7; } }

.litis-trial .hint-bar { background: rgba(112,163,60,0.08); border-bottom: 1px solid rgba(112,163,60,0.15); padding: 9px 20px; display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.litis-trial .hint-bar-text { font-size: 12px; color: var(--green); font-weight: 500; }

.litis-trial .transcript-area { flex: 1; overflow-y: auto; padding: 20px 24px; }
.litis-trial .transcript-area::-webkit-scrollbar { width: 5px; }
.litis-trial .transcript-area::-webkit-scrollbar-thumb { background: var(--bg3); border-radius: 2px; }
.litis-trial .transcript-empty { height: 100%; display: flex; align-items: center; justify-content: center; opacity: 0.4; }
.litis-trial .transcript-empty p { font-size: 15px; color: var(--muted); font-style: italic; }

.litis-trial .msg { padding: 14px 0; border-bottom: 1px solid var(--border2); animation: litis-fadein 0.3s ease both; }
.litis-trial .msg:last-child { border-bottom: none; }
@keyframes litis-fadein { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.litis-trial .msg-header { display: flex; align-items: center; gap: 10px; margin-bottom: 7px; }
.litis-trial .msg-role-badge { font-size: 10px; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; padding: 2px 8px; border-radius: 4px; }
.litis-trial .role-sec { background: rgba(176,170,158,0.18); color: #8A8078; }
.litis-trial .role-fis { background: var(--fis-bg); color: var(--fis-color); }
.litis-trial .role-def { background: var(--def-bg); color: var(--def-color); }
.litis-trial .role-test1 { background: rgba(46,158,140,0.12); color: #2E9E8C; }
.litis-trial .role-test2 { background: rgba(192,148,24,0.14); color: #A07810; }
.litis-trial .role-test3 { background: rgba(74,127,181,0.12); color: #4A7FB5; }
.litis-trial .role-test4 { background: rgba(168,74,107,0.12); color: #A84A6B; }
.litis-trial .role-juez { background: rgba(91,91,168,0.12); color: #5B5BA8; }
.litis-trial .msg-obj-tag { font-size: 10px; font-weight: 700; letter-spacing: 0.06em; color: var(--accent); }
.litis-trial .msg-text { font-size: 14px; line-height: 1.7; color: var(--text); max-width: 720px; }
.litis-trial .msg-text em { color: var(--muted); font-style: italic; }
.litis-trial .msg-text strong { color: var(--dark); font-weight: 600; }
.litis-trial .msg.sec-msg { background: var(--white); border: 1px solid var(--border); border-radius: var(--r); padding: 16px 20px; margin-bottom: 8px; }
.litis-trial .msg.obj-msg { background: rgba(196,87,42,0.04); border-left: 2px solid var(--accent); padding-left: 14px; }
.litis-trial .sys-line { text-align: center; border: none; padding: 8px 0; }
.litis-trial .sys-line span { font-size: 11px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--light); }

.litis-trial .processing { display: flex; align-items: center; gap: 10px; padding: 12px 16px; margin: 4px 0; background: rgba(46,158,140,0.07); border: 1px solid rgba(46,158,140,0.15); border-radius: 0 12px 12px 12px; width: fit-content; }
.litis-trial .processing-dots { display: flex; align-items: center; gap: 4px; }
.litis-trial .processing-dots span { width: 7px; height: 7px; border-radius: 50%; background: #2E9E8C; animation: litis-typing 1.4s infinite ease-in-out; }
.litis-trial .processing-dots span:nth-child(2) { animation-delay: 0.16s; }
.litis-trial .processing-dots span:nth-child(3) { animation-delay: 0.32s; }
@keyframes litis-typing { 0%,60%,100% { transform: translateY(0); opacity: 0.35; } 30% { transform: translateY(-5px); opacity: 1; } }

/* Voz / micrófono */
.litis-trial .speaking-bar { padding: 6px 20px; border-top: 1px solid var(--border); background: var(--white); display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
.litis-trial .wave-bar { width: 3px; border-radius: 2px; background: var(--dark); min-height: 4px; animation: litis-wave 1s ease-in-out infinite; }
.litis-trial .wave-blue { background: var(--blue); }
.litis-trial .wave-green { background: var(--green); }
@keyframes litis-wave { 0%,100% { height: 4px; } 50% { height: 16px; } }
.litis-trial .speaking-label { font-size: 9px; color: var(--muted); font-weight: 700; letter-spacing: 0.08em; margin-left: 6px; }

.litis-trial .mic-status { padding: 10px 20px; border-top: 1px solid var(--border); background: var(--bg2); display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.litis-trial .mic-status.mic-rt { background: rgba(74,127,181,0.08); }
.litis-trial .mic-status.mic-connecting { background: rgba(91,91,168,0.06); }
.litis-trial .mic-status.mic-error { background: rgba(196,87,42,0.06); }
.litis-trial .mic-status.mic-listening { background: rgba(112,163,60,0.07); }
.litis-trial .mic-waves { display: flex; align-items: center; gap: 3px; flex-shrink: 0; }
.litis-trial .mic-status-text { font-size: 11px; font-weight: 700; letter-spacing: 0.04em; flex: 1; }
.litis-trial .mic-status-text.rt { color: var(--blue); }
.litis-trial .mic-status-text.grn { color: var(--green); }
.litis-trial .mic-status-text.err { color: var(--accent); }
.litis-trial .mic-transcript { color: var(--text); font-weight: 500; font-style: italic; margin-left: 6px; }
.litis-trial .mic-status-hint { font-size: 10px; color: var(--light); font-weight: 600; flex-shrink: 0; }
.litis-trial .mic-spinner { width: 12px; height: 12px; border: 2px solid var(--border); border-top-color: var(--blue); border-radius: 50%; animation: litis-spin 0.8s linear infinite; flex-shrink: 0; }
@keyframes litis-spin { to { transform: rotate(360deg); } }

/* Objeción */
.litis-trial .objection-menu { padding: 8px 16px; border-top: 2px solid var(--accent); background: rgba(196,87,42,0.06); display: flex; flex-wrap: wrap; gap: 5px; align-items: center; flex-shrink: 0; }
.litis-trial .objection-menu-label { font-size: 9px; font-weight: 700; letter-spacing: 0.08em; color: var(--accent); margin-right: 4px; }
.litis-trial .objection-chip { padding: 5px 11px; border-radius: 5px; background: var(--white); border: 1px solid var(--accent); color: var(--accent); font-size: 11px; font-weight: 600; font-family: var(--sans); cursor: pointer; transition: all 0.15s; }
.litis-trial .objection-chip:hover { background: var(--accent); color: white; }
.litis-trial .objection-close { padding: 5px 10px; border-radius: 5px; border: 1px solid var(--border); background: none; color: var(--muted); font-size: 11px; cursor: pointer; font-family: var(--sans); }

/* Input */
.litis-trial .input-bar { background: var(--white); border-top: 1px solid var(--border); padding: 12px 16px; display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.litis-trial .input-mic { width: 38px; height: 38px; border-radius: 50%; flex-shrink: 0; background: var(--bg2); border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 15px; transition: all 0.2s; }
.litis-trial .input-mic:hover:not(:disabled) { background: var(--bg3); }
.litis-trial .input-mic:disabled { opacity: 0.4; cursor: not-allowed; }
.litis-trial .input-mic.mic-obj-active { background: var(--accent); border-color: var(--accent); }
.litis-trial .input-field { flex: 1; font-family: var(--sans); font-size: 14px; background: var(--bg); border: 1px solid var(--border); border-radius: var(--r); padding: 10px 16px; color: var(--text); outline: none; transition: border-color 0.2s; }
.litis-trial .input-field:focus { border-color: var(--accent); background: var(--white); }
.litis-trial .input-field::placeholder { color: var(--light); }
.litis-trial .input-field:disabled { opacity: 0.6; }
.litis-trial .input-send { font-family: var(--sans); font-size: 13px; font-weight: 500; background: var(--dark); color: white; border: none; padding: 10px 20px; border-radius: var(--r); cursor: pointer; transition: all 0.2s; flex-shrink: 0; }
.litis-trial .input-send:hover:not(:disabled) { opacity: 0.85; transform: translateY(-1px); }
.litis-trial .input-send:disabled { background: var(--bg3); color: var(--light); cursor: not-allowed; }

/* ── MOBILE — tablet & smaller ── */
@media (max-width: 900px) {
  .litis-trial .trial-body { grid-template-columns: 1fr; }
  .litis-trial .trial-sidebar { display: none; }
  .litis-trial .topbar-case { display: none; }
}

/* ── MOBILE PHONE (≤ 640px wide) ── */
@media (max-width: 640px) {
  /* Topbar comprimida: solo lo esencial */
  .litis-trial .trial-topbar { height: 48px; padding: 0 10px; }
  .litis-trial .topbar-brand { padding-right: 10px; border-right: none; }
  .litis-trial .topbar-brand img { height: 18px; }
  .litis-trial .topbar-center { padding: 0 8px; gap: 6px; border-right: none; }
  .litis-trial .topbar-badge { font-size: 9px; padding: 3px 7px; }
  .litis-trial .topbar-progress, .litis-trial .topbar-witness, .litis-trial .topbar-rt { display: none; }
  .litis-trial .topbar-timer { font-size: 15px; padding: 0 8px; min-width: 56px; border-right: none; }
  .litis-trial .topbar-actions { gap: 4px; padding-left: 6px; }
  .litis-trial .topbar-actions .tbtn { padding: 5px 8px; font-size: 10px; }
  /* Ocultar botones secundarios en vertical, dejar solo críticos */
  .litis-trial .topbar-actions .tbtn-ghost { display: none; }

  /* Transcript compacto */
  .litis-trial .transcript-area { padding: 12px 14px; }
  .litis-trial .msg-text { font-size: 13px; line-height: 1.6; }
  .litis-trial .msg.sec-msg { padding: 12px 14px; }
  .litis-trial .msg-role-badge { font-size: 9px; padding: 2px 6px; }

  /* Input bar pegado abajo */
  .litis-trial .input-bar { padding: 10px 12px; gap: 8px; }
  .litis-trial .input-mic { width: 36px; height: 36px; }
  .litis-trial .input-field { font-size: 13px; padding: 9px 13px; }
  .litis-trial .input-send { padding: 9px 14px; font-size: 12px; }

  /* Mic status compacto */
  .litis-trial .mic-status { padding: 8px 14px; gap: 8px; }
  .litis-trial .mic-status-text { font-size: 10px; }
  .litis-trial .mic-status-hint { display: none; }
  .litis-trial .mic-transcript { display: none; }

  /* Objection menu wrapeable */
  .litis-trial .objection-menu { padding: 6px 10px; gap: 4px; }
  .litis-trial .objection-chip { padding: 4px 9px; font-size: 10px; }

  /* Ruling/AI/Hint bars compactos */
  .litis-trial .trial-ruling-bar, .litis-trial .trial-ai-bar, .litis-trial .hint-bar {
    padding: 8px 14px;
  }
  .litis-trial .trial-ruling-text, .litis-trial .hint-bar-text { font-size: 12px; }
  .litis-trial .trial-ai-bar span { font-size: 11px; }
}

/* ── OVERLAY DE ORIENTACIÓN — pedile al usuario que rote a horizontal ── */
.litis-trial .rotate-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: var(--dark); color: white;
  display: none;
  flex-direction: column; align-items: center; justify-content: center;
  gap: 22px; padding: 30px;
  font-family: var(--sans);
}
.litis-trial .rotate-overlay img.rotate-logo { height: 28px; opacity: 0.7; margin-bottom: 8px; }
.litis-trial .rotate-overlay .rotate-icon {
  width: 80px; height: 80px;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid rgba(255,255,255,0.25); border-radius: 16px;
  animation: litis-rotate-hint 2.4s ease-in-out infinite;
}
.litis-trial .rotate-overlay .rotate-icon svg { width: 44px; height: 44px; fill: white; opacity: 0.85; }
@keyframes litis-rotate-hint {
  0%, 100% { transform: rotate(0deg); }
  50% { transform: rotate(-90deg); }
}
.litis-trial .rotate-overlay h2 {
  font-size: 18px; font-weight: 500; color: white;
  letter-spacing: -0.01em; text-align: center; max-width: 280px;
  line-height: 1.4;
}
.litis-trial .rotate-overlay p {
  font-size: 13px; color: rgba(255,255,255,0.55);
  text-align: center; max-width: 300px; line-height: 1.6;
}
.litis-trial .rotate-overlay .rotate-skip {
  margin-top: 8px; font-family: var(--sans); font-size: 12px;
  background: none; border: 1px solid rgba(255,255,255,0.2);
  color: rgba(255,255,255,0.65); padding: 8px 18px; border-radius: 8px;
  cursor: pointer; transition: all 0.2s;
}
.litis-trial .rotate-overlay .rotate-skip:hover { border-color: rgba(255,255,255,0.4); color: white; }

/* Solo se muestra en teléfonos en vertical */
@media (max-width: 640px) and (orientation: portrait) {
  .litis-trial .rotate-overlay.show { display: flex; }
}
`;

export default function Trial({ cfg, onEval, onBack, showTutorial }) {
  const { userRole, userName, difficulty, caseData, witnessQueue } = cfg;

  const [msgs,           setMsgs]           = useState([]);
  const [input,          setInput]          = useState("");
  const [loading,        _setLoading]       = useState(false);
  const [speaking,       _setSpeaking]      = useState(false);
  const [speakerName,    setSpeakerName]    = useState("");
  const [judgeRuling,    setJudgeRuling]    = useState(null);
  const [objectionMode,  setObjectionMode]  = useState(false);
  const [elapsed,        setElapsed]        = useState(0);
  const [currentTip,     setCurrentTip]     = useState(null);
  const [trialPhase,     setTrialPhase]     = useState("interrogatorio");
  const [activeRole,     setActiveRole]     = useState(null);
  const [witnessIdx,     setWitnessIdx]     = useState(0);
  const [trialClosed,    setTrialClosed]    = useState(false);
  const [tutorialStep,   setTutorialStep]   = useState(showTutorial ? 0 : -1);
  const [reportLoading,  setReportLoading]  = useState(null);
  const [micListening,   setMicListening]   = useState(false);
  const [lastTranscript, setLastTranscript] = useState("");
  const [micError,       setMicError]       = useState(false);
  const [isPaused,       setIsPaused]       = useState(false);
  const [rotateSkipped,  setRotateSkipped]  = useState(false);
  const [rtConnecting,   setRtConnecting]   = useState(false);
  const [rtError,        setRtError]        = useState(null);

  const initiated         = useRef(false);
  const cancelled         = useRef(false);
  const paused            = useRef(false);
  const endRef            = useRef(null);
  const msgsRef           = useRef([]);
  const phaseRef          = useRef("interrogatorio");
  const activeRoleRef     = useRef(null);
  const witnessIdxRef     = useRef(0);
  const aiRunning         = useRef(false);
  const witnessHistory    = useRef({});
  const objectionPending  = useRef(false);
  const lastObjSustained  = useRef(false);
  const speakingRef       = useRef(false);
  const loadingRef        = useRef(false);
  const sendMessageRef    = useRef(null);
  const pendingTranscriptRef = useRef(null);

  const realtimeMsgRef        = useRef(null);
  const userMsgPlaceholderRef = useRef(null);
  const lastTranscriptRef     = useRef("");
  const currentWitnessRef     = useRef(null);
  const currentPhaseRef       = useRef("interrogatorio");
  const adversaryRunningRef   = useRef(false);
  const trialFrozenRef        = useRef(false);
  const onRTUserTranscriptRef = useRef(null);
  const onRTWitnessDeltaRef   = useRef(null);
  const onRTWitnessDoneRef    = useRef(null);
  const onRTSpeechDetectedRef = useRef(null);

  const currentWitness = witnessQueue[witnessIdx];

  const addMsg = msg => {
    const m = { ...msg, _id: Date.now() + Math.random() };
    msgsRef.current = [...msgsRef.current, m];
    setMsgs([...msgsRef.current]);
    return m;
  };
  const updateMsg = (id, content) => {
    msgsRef.current = msgsRef.current.map(m => m._id === id ? { ...m, content } : m);
    setMsgs([...msgsRef.current]);
  };
  const setPhase  = p => { phaseRef.current = p; currentPhaseRef.current = p; setTrialPhase(p); };
  const setActive = r => { activeRoleRef.current = r; setActiveRole(r); };
  const setWIdx   = i => { witnessIdxRef.current = i; setWitnessIdx(i); };
  const setLoading  = val => { loadingRef.current  = val; _setLoading(val); };
  const setSpeaking = val => { speakingRef.current = val; _setSpeaking(val); };

  const addWitnessHistory = (idx, q, a) => {
    if (!witnessHistory.current[idx]) witnessHistory.current[idx] = [];
    witnessHistory.current[idx].push({ q, a });
  };
  const getWitnessHistory = idx => witnessHistory.current[idx] || [];
  const delay = ms => new Promise(r => setTimeout(r, ms));
  const waitIfPaused = async () => {
    while ((paused.current || objectionPending.current) && !cancelled.current) await delay(200);
  };

  const judgeQueueRef      = useRef(Promise.resolve());
  const judgeActiveRef     = useRef(false);

  const freezeTrial = useCallback(() => {
    trialFrozenRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unfreezeTrial = useCallback(() => {
    trialFrozenRef.current = false;
  }, []);

  const waitIfFrozen = async () => {
    while (trialFrozenRef.current && !cancelled.current) await delay(100);
  };

  const speak = (text, character, witnessName, witnessGender) => {
    if (paused.current || trialFrozenRef.current || judgeActiveRef.current) return Promise.resolve();
    return speakText(
      text, character,
      () => { setSpeaking(true);  setSpeakerName(character); },
      () => { setSpeaking(false); setSpeakerName(""); },
      witnessName, witnessGender
    );
  };

  const speakJudge = useCallback((text) => {
    const task = () => new Promise(resolve => {
      if (cancelled.current) { resolve(); return; }
      judgeActiveRef.current = true;
      stopSpeech();
      speakText(
        text, "juez",
        () => { setSpeaking(true);  setSpeakerName("juez"); },
        () => { setSpeaking(false); setSpeakerName(""); judgeActiveRef.current = false; resolve(); }
      );
    });
    judgeQueueRef.current = judgeQueueRef.current.then(task);
    return judgeQueueRef.current;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flushJudgeQueue = useCallback(() => {
    judgeQueueRef.current = Promise.resolve();
    judgeActiveRef.current = false;
  }, []);

  const normalizeObjText = t =>
    t.replace(/\bOBJECIÓN\b/g,"Objeción").replace(/\bOBJECION\b/g,"Objeción")
     .replace(/¡OBJECIÓN!/g,"¡Objeción!").replace(/¡OBJECION!/g,"¡Objeción!")
     .replace(/\s*—\s*/g,", ");

  useEffect(() => {
    if (!loading && !speaking && pendingTranscriptRef.current) {
      const pending = pendingTranscriptRef.current;
      pendingTranscriptRef.current = null;
      sendMessageRef.current?.(pending);
    }
  }, [loading, speaking]);

  const {
    connect:             connectRealtime,
    disconnect:          disconnectRealtime,
    interrupt:           interruptRealtime,
    sendWitnessQuestion,
    waitUntilSilent,
    muteMic,
    enableVAD,
    disableVAD,
    isConnected:         rtConnected,
  } = useRealtimeWitness({
    onUserSpeechDetected: ()  => onRTSpeechDetectedRef.current?.(),
    onUserTranscript:     (t) => onRTUserTranscriptRef.current?.(t),
    onWitnessTextDelta:   (d) => onRTWitnessDeltaRef.current?.(d),
    onWitnessDone:        (t) => onRTWitnessDoneRef.current?.(t),
    onSpeakingChange:     (isSp, character) => { setSpeaking(isSp); setSpeakerName(isSp ? character : ""); },
  });

  const connectRealtimeForUser = useCallback((witness, phase, history, muteAfter = false) => {
    currentWitnessRef.current = witness;
    realtimeMsgRef.current = null;
    lastTranscriptRef.current = "";
    userMsgPlaceholderRef.current = null;
    setRtError(null);
    setRtConnecting(true);
    const prompt = buildWitnessPrompt(caseData, witness, phase, history);
    connectRealtime(witness, prompt)
      .then(() => {
        setRtConnecting(false);
        setRtError(null);
        if (muteAfter) muteMic();
      })
      .catch(e => {
        setRtConnecting(false);
        setRtError(e?.message || "Error al conectar Realtime");
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectRealtime, muteMic]);

  const handleTranscript = useCallback((text) => {
    if (!text?.trim()) return;
    if (objectionResolveRef.current) { objectionResolveRef.current(text); objectionResolveRef.current = null; return; }
    if (activeRoleRef.current !== "user") return;
    if (phaseRef.current === "cerrado") return;
    setLastTranscript(text);
    if (loadingRef.current || speakingRef.current) { pendingTranscriptRef.current = text; return; }
    if (isTurnEnd(text)) {
      setActive("none");
      handlePhaseTransition(witnessIdxRef.current, witnessQueue[witnessIdxRef.current], phaseRef.current);
    } else {
      sendMessageRef.current?.(normalizeTranscript(text));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { startListening, stopListening, destroyMic } = useMicrophone({ onTranscript: handleTranscript });

  useEffect(() => {
    const shouldListen = (
      activeRoleRef.current === "user" && !rtConnected && !rtConnecting && !objectionPending.current
    ) && !trialClosed && !speaking && !isPaused;
    if (shouldListen) {
      const t = setTimeout(() => {
        if (!speakingRef.current && !objectionPending.current) {
          setMicListening(true); setLastTranscript("");
          startListening().catch(e => { setMicError(e?.message || "Micrófono no disponible"); setMicListening(false); });
        }
      }, 300);
      return () => clearTimeout(t);
    } else { setMicListening(false); stopListening(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRole, trialClosed, loading, speaking, isPaused, rtConnected, rtConnecting]);

  useEffect(() => { const t = setInterval(() => setElapsed(s=>s+1), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior:"smooth" }); }, [msgs]);
  useEffect(() => {
    const tips=TIPS[trialPhase]||[]; let idx=0;
    const show=()=>{ setCurrentTip(tips[idx%tips.length]); idx++; };
    show(); const t=setInterval(show,9000); return ()=>clearInterval(t);
  }, [trialPhase]);

  const handleRTSpeechDetected = () => {
    if (phaseRef.current === "cerrado") return;
    userMsgPlaceholderRef.current = addMsg({ role:"user", content:"…" });
  };

  // Extrae el tipo de objeción de un texto del adversario IA tipo
  // "¡Objeción! Sugestiva — la pregunta sugiere…" → "Sugestiva"
  const extractObjType = (advText) => {
    if (!advText) return "Procesal";
    const m = advText.match(/¡?Objeci[oó]n!?[\s,:]+([^—\-–.,]+?)\s*[—\-–.,]/i);
    return m ? m[1].trim() : "Procesal";
  };

  const handleRTObjection = async (adv, originalQuestion) => {
    interruptRealtime();
    stopSpeech();
    freezeTrial();
    objectionPending.current = true;
    await delay(150);
    const advSpeaker = userRole==="fiscal" ? "defensor" : "fiscal";
    const cleanAdv   = normalizeObjText(stripMd(adv));
    addMsg({ role:"assistant", speaker:advSpeaker, content:cleanAdv, isObjection:true });
    await speak(cleanAdv, advSpeaker);
    const ruling = await callClaude(
      [{role:"user",content:"Resuelve la objeción de inmediato según el criterio del Código Orgánico Integral Penal y el contexto provisto."}],
      buildJudgeRulingPrompt({
        userRole,
        phase: phaseRef.current,
        isUserObjecting: false,                  // la IA objetó al usuario
        objectionType: extractObjType(adv),
        originalQuestion: originalQuestion || "",
        witnessHistory: getWitnessHistory(witnessIdxRef.current),
      })
    );
    const cr = stripMd(ruling).replace(/DECISION:|FUNDAMENTO:|INSTRUCCION:/g,"").trim();
    // Capturar si el juez sostuvo o no — afecta el flujo posterior
    lastObjSustained.current = ruling.toLowerCase().includes("ha lugar") &&
                               !ruling.toLowerCase().includes("no ha lugar");
    setJudgeRuling(cr); addMsg({ role:"assistant", speaker:"juez", content:cr });
    await speakJudge(cr); setTimeout(()=>setJudgeRuling(null), 10000);
    objectionPending.current = false;
    unfreezeTrial();
    const wit = currentWitnessRef.current;
    if (wit && activeRoleRef.current==="user" && !cancelled.current) {
      const hist = getWitnessHistory(witnessIdxRef.current);
      adversaryRunningRef.current = false;
      connectRealtimeForUser(wit, currentPhaseRef.current, hist);
      setTimeout(() => enableVAD(), 1500);
    }
  };

  const handleRTUserTranscript = async (text) => {
    if (!text?.trim() || phaseRef.current==="cerrado") return;
    if (adversaryRunningRef.current) return;
    adversaryRunningRef.current = true;
    setLastTranscript(text); lastTranscriptRef.current = text;
    if (userMsgPlaceholderRef.current) { updateMsg(userMsgPlaceholderRef.current._id, text.trim()); userMsgPlaceholderRef.current = null; }
    else addMsg({ role:"user", content:text.trim() });
    if (isTurnEnd(text)) {
      adversaryRunningRef.current = false;
      interruptRealtime(); disconnectRealtime(); setActive("none");
      handlePhaseTransition(witnessIdxRef.current, witnessQueue[witnessIdxRef.current], phaseRef.current);
      return;
    }
    try {
      const adv = await callClaude([{role:"user",content:`El abogado preguntó: "${text}"`}], buildAdversaryPrompt(caseData,userRole,difficulty));
      if (adv?.startsWith("CONDUCTA_IMPROPIA:")) {
        interruptRealtime(); stopSpeech(); sabotageCount.current += 1;
        if (sabotageCount.current >= 2) {
          const t2 = await callClaude([{role:"user",content:"Termina la audiencia por conducta impropia."}], buildJudgeTerminateSabotagePrompt());
          addMsg({role:"assistant",speaker:"juez",content:stripMd(t2)}); await speakJudge(stripMd(t2));
          setPhase("cerrado"); setActive("none"); setTrialClosed(true);
        } else {
          const w2 = await callClaude([{role:"user",content:"Llama la atención."}], buildJudgeWarningSabotagePrompt());
          setJudgeRuling(stripMd(w2)); addMsg({role:"assistant",speaker:"juez",content:stripMd(w2)});
          await speakJudge(stripMd(w2)); setTimeout(()=>setJudgeRuling(null),12000);
        }
        adversaryRunningRef.current = false;
        return;
      }
      if (adv && !adv.includes("SIN_OBJECION")) await handleRTObjection(adv, text);
    } catch (e) { console.error("RT adversary error:", e); }
    adversaryRunningRef.current = false;
  };

  const handleRTWitnessDelta = (delta) => {
    const wit = witnessQueue[witnessIdxRef.current];
    if (!wit || !delta) return;
    if (!realtimeMsgRef.current) realtimeMsgRef.current = addMsg({ role:"assistant", speaker:wit.tipo, content:"…", witnessName:wit.nombre });
    const existing = msgsRef.current.find(m => m._id===realtimeMsgRef.current?._id);
    const prev = existing?.content==="…" ? "" : (existing?.content||"");
    const updated = stripMd(prev+delta);
    if (updated) updateMsg(realtimeMsgRef.current._id, updated);
  };

  const handleRTWitnessDone = (fullText) => {
    const cleaned = stripMd(fullText);
    if (cleaned && lastTranscriptRef.current) addWitnessHistory(witnessIdxRef.current, lastTranscriptRef.current, cleaned);
    realtimeMsgRef.current = null;
  };

  onRTSpeechDetectedRef.current = handleRTSpeechDetected;
  onRTUserTranscriptRef.current = handleRTUserTranscript;
  onRTWitnessDeltaRef.current   = handleRTWitnessDelta;
  onRTWitnessDoneRef.current    = handleRTWitnessDone;

  useEffect(() => {
    if (!initiated.current) { initiated.current = true; openAudience(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAudience = async () => {
    setLoading(true);
    try {
      const secText = await callClaude([{role:"user",content:"Abre la audiencia."}], buildSecretaryOpeningPrompt(cfg));
      if (cancelled.current) return;
      await waitIfPaused();
      addMsg({ role:"assistant", speaker:"secretaria", content:stripMd(secText) });
      await speak(stripMd(secText), "secretaria");
    } catch { addMsg({ role:"assistant", speaker:"secretaria", content:"Se declara instalado el Tribunal.", isSystem:true }); }
    setLoading(false);
    await openWitness(0, witnessQueue[0]);
  };

  const openWitness = async (idx, witness) => {
    if (cancelled.current || !witness) return;
    setWIdx(idx); setPhase("interrogatorio");
    currentWitnessRef.current = witness;
    const firstRole   = witness.startRole;
    const firstPerson = firstRole==="user" ? userName : (caseData.abogado_ia || (userRole==="fiscal" ? "la Defensa" : "la Fiscalía del Estado"));
    setLoading(true);
    try {
      const judgeText = await callClaude([{role:"user",content:"Llama al testigo y tómale el juramento."}], buildJudgeWitnessOpeningPrompt(witness,firstPerson,"interrogatorio"));
      if (cancelled.current) return;
      await waitIfPaused();
      addMsg({ role:"assistant", speaker:"juez", content:stripMd(judgeText) });
      await speakJudge(stripMd(judgeText));
      if (!cancelled.current) {
        const oathResp = await callClaude([{role:"user",content:"Responde al juramento."}], buildWitnessOathResponsePrompt(witness));
        if (!cancelled.current) {
          await waitIfPaused();
          addMsg({ role:"assistant", speaker:witness.tipo, content:stripMd(oathResp), witnessName:witness.nombre });
          await speak(stripMd(oathResp), witness.tipo, witness.nombre, witness.genero);
        }
      }
      if (!cancelled.current) {
        const floorText = await callClaude([{role:"user",content:"Cede la palabra."}], buildJudgeAfterOathPrompt(firstPerson));
        if (!cancelled.current) {
          await waitIfPaused();
          addMsg({ role:"assistant", speaker:"juez", content:stripMd(floorText) });
          await speakJudge(stripMd(floorText));
        }
      }
    } catch { addMsg({ role:"assistant", speaker:"sistema", content:`${witness.nombre} toma el estrado.`, isSystem:true }); }
    setLoading(false);
    setActive(firstRole);
    if (!cancelled.current) {
      const muteAfter = firstRole === "ai";
      connectRealtimeForUser(witness, "interrogatorio", [], muteAfter);
      if (firstRole !== "ai") {
        setTimeout(() => enableVAD(), 1500);
      } else {
        setTimeout(() => disableVAD(), 1500);
      }
    }
    if (firstRole==="ai" && !aiRunning.current) {
      aiRunning.current = true;
      runAiInterrogation(idx, witness, "interrogatorio");
    }
  };

  const runAiInterrogation = async (idx, witness, phase) => {
    if (!aiRunning.current) return;
    const aiRole  = userRole==="fiscal" ? "defensor" : "fiscal";
    const qCounts = { basico:2, intermedio:3, avanzado:4 };
    const nQ      = qCounts[difficulty] || 3;
    setLoading(true);
    for (let i=0; i<nQ; i++) {
      if (cancelled.current) break;
      await waitIfPaused();
      await waitIfFrozen();
      if (cancelled.current) break;
      try {
        const prevQ = (witnessHistory.current[idx]||[]).filter(h=>h.isAi).map(h=>h.q).join("\n");
        const question = await callClaude([{role:"user",content:`Genera la pregunta numero ${i+1}.`}], buildAILawyerQuestionPrompt(caseData,witness,aiRole,phase,i+1,prevQ));
        if (cancelled.current) break;
        await waitIfFrozen();
        if (cancelled.current) break;
        const cleanQ = stripMd(question);
        addMsg({ role:"assistant", speaker:aiRole, content:cleanQ, isAiQuestion:true });
        await speak(cleanQ, aiRole);
        if (cancelled.current) break;

        // Esperar a que termine cualquier objeción del usuario y a que el juez termine de hablar
        while (objectionPending.current) await delay(200);
        await waitIfFrozen();
        while (speakingRef.current) await delay(100);
        if (cancelled.current) break;

        // ── FIX BUG 1 ──────────────────────────────────────────────────────
        // Si la objeción fue SOSTENIDA, NO mandar la pregunta original al
        // testigo. Generar reformulación primero y enviar SOLO esa.
        // (Antes: el código enviaba la original al testigo y recién después
        // chequeaba si fue sostenida → el testigo respondía la pregunta vetada)
        if (lastObjSustained.current) {
          lastObjSustained.current = false;
          const prevQ2 = (witnessHistory.current[idx]||[]).filter(h=>h.isAi).map(h=>h.q).join("\n");
          const rephrased = await callClaude(
            [{role:"user",content:"Tu pregunta anterior fue objetada y el juez sostuvo la objeción. Reformúlala respetando la regla que motivó la objeción. NO repitas la pregunta original."}],
            buildAILawyerQuestionPrompt(caseData,witness,aiRole,phase,i+1,prevQ2)
          );
          if (cancelled.current) break;
          await waitIfFrozen();
          const cleanR = stripMd(rephrased);
          addMsg({ role:"assistant", speaker:aiRole, content:cleanR, isAiQuestion:true });
          await speak(cleanR, aiRole);
          // Por si el usuario objeta también la reformulación
          while (objectionPending.current) await delay(200);
          await waitIfFrozen();
          while (speakingRef.current) await delay(100);
          if (cancelled.current) break;
          const witText2 = await sendWitnessQuestion(cleanR);
          if (cancelled.current) break;
          await waitUntilSilent();
          await waitIfFrozen();
          if (cancelled.current) break;
          const clean2 = stripMd((witText2||"").replace(/\[OBJ:[^\]]+\]/gi,"").trim());
          if (clean2) {
            addWitnessHistory(idx, cleanR, clean2);
            if (witnessHistory.current[idx]?.length) witnessHistory.current[idx][witnessHistory.current[idx].length-1].isAi = true;
          }
          continue; // siguiente pregunta del bucle
        }

        // ── Camino normal: sin objeción sostenida → enviar la pregunta al testigo
        const witText = await sendWitnessQuestion(cleanQ);
        if (cancelled.current) break;
        await waitUntilSilent();
        await waitIfFrozen();
        if (cancelled.current) break;
        const clean = stripMd((witText||"").replace(/\[OBJ:[^\]]+\]/gi,"").trim());
        if (clean) {
          addWitnessHistory(idx, cleanQ, clean);
          if (witnessHistory.current[idx]?.length) witnessHistory.current[idx][witnessHistory.current[idx].length-1].isAi = true;
        }
      } catch {}
    }
    setLoading(false);
    await waitUntilSilent();
    await waitIfFrozen();
    if (!cancelled.current) {
      try {
        const closingText = await callClaude([{role:"user",content:"Cierra tu interrogatorio."}], buildAILawyerClosingPrompt(aiRole,witness));
        await waitIfFrozen();
        if (!cancelled.current) { addMsg({role:"assistant",speaker:aiRole,content:stripMd(closingText)}); await speak(stripMd(closingText),aiRole); }
      } catch {}
    }
    await waitIfFrozen();
    aiRunning.current = false;
    if (!cancelled.current) await handlePhaseTransition(idx, witness, phase);
  };

  const handlePhaseTransition = async (idx, witness, fromPhase) => {
    if (cancelled.current) return;
    await waitIfPaused();
    await waitIfFrozen();
    if (fromPhase==="interrogatorio") {
      const nextRole   = witness.startRole==="user" ? "ai" : "user";
      const nextPerson = nextRole==="user" ? userName : (caseData.abogado_ia || (userRole==="fiscal" ? "la Defensa" : "la Fiscalía del Estado"));
      setLoading(true);
      try {
        const transText = await callClaude([{role:"user",content:"Anuncia el contrainterrogatorio."}], buildJudgeTransitionPrompt(witness,nextPerson));
        if (cancelled.current) return;
        await waitIfPaused();
        addMsg({ role:"assistant", speaker:"juez", content:stripMd(transText) });
        await speakJudge(stripMd(transText));
      } catch { addMsg({ role:"assistant", speaker:"sistema", content:"Inicia el contrainterrogatorio.", isSystem:true }); }
      setLoading(false);
      setPhase("contrainterrogatorio");
      setActive(nextRole);
      if (!cancelled.current) {
        disconnectRealtime();
        const history = getWitnessHistory(idx);
        connectRealtimeForUser(witness, "contrainterrogatorio", history, nextRole === "ai");
        if (nextRole === "ai") {
          setTimeout(() => disableVAD(), 1500);
        } else {
          setTimeout(() => enableVAD(), 1500);
        }
      }
      if (nextRole==="ai" && !cancelled.current) {
        aiRunning.current = true;
        runAiInterrogation(idx, witness, "contrainterrogatorio");
      }
    } else {
      const nextIdx = idx+1;
      if (nextIdx < witnessQueue.length) {
        const nextWitness = witnessQueue[nextIdx];
        const nextFirst   = nextWitness.startRole==="user" ? userName : (caseData.abogado_ia || (userRole==="fiscal" ? "la Defensa" : "la Fiscalía del Estado"));
        setLoading(true);
        try {
          const nwText = await callClaude([{role:"user",content:"Llama al siguiente testigo."}], buildJudgeNextWitnessPrompt(witness,nextWitness,nextFirst));
          if (cancelled.current) return;
          await waitIfPaused();
          addMsg({ role:"assistant", speaker:"juez", content:stripMd(nwText) });
          await speakJudge(stripMd(nwText));
        } catch { addMsg({ role:"assistant", speaker:"sistema", content:`${nextWitness.nombre} al estrado.`, isSystem:true }); }
        setLoading(false);
        aiRunning.current = false;
        disconnectRealtime();
        await openWitness(nextIdx, nextWitness);
      } else {
        await handleCloseTrial();
      }
    }
  };

  const handleCloseTrial = async () => {
    if (cancelled.current) return;
    disconnectRealtime();
    setPhase("cerrado"); setActive("none"); setTrialClosed(true); setLoading(true);
    try {
      const closeText = await callClaude([{role:"user",content:"Clausura la audiencia."}], buildJudgeClosingPrompt(caseData,userRole,userName));
      if (cancelled.current) return;
      addMsg({ role:"assistant", speaker:"juez", content:stripMd(closeText) });
      await speakJudge(stripMd(closeText));
    } catch { addMsg({ role:"assistant", speaker:"sistema", content:"Audiencia clausurada.", isSystem:true }); }
    setLoading(false);
  };

  const sendMessage = async text => {
    if (rtConnected) return;
    if (!text.trim()||loadingRef.current||activeRoleRef.current!=="user"||trialClosed||phaseRef.current==="cerrado") return;
    setInput(""); setLoading(true); setJudgeRuling(null); stopSpeech();
    addMsg({ role:"user", content:text.trim() });
    if (tutorialStep===2) setTutorialStep(3);
    const idx=witnessIdxRef.current; const wit=witnessQueue[idx]; const phase=phaseRef.current;
    try {
      const hist=getWitnessHistory(idx);
      const VAMP_BY_TEMP={
        cooperativo:["Sí...","Mire...","Bueno..."],nervioso:["Eh... bueno...","A ver...","Pues..."],
        hostil:["...","Hmm.","¿Qué?"],evasivo:["Bueno, eso es...","Eh..."],
        tecnico:["Bien...","A ver..."],dramatico:["Dios mío...","Eso es..."],
        seguro:["Claro.","Por supuesto."],desconfiado:["¿Por qué me pregunta eso?","Hmm..."],
      };
      const vamps=VAMP_BY_TEMP[wit.temperamento]||["..."];
      const vamp=vamps[Math.floor(Math.random()*vamps.length)];
      const vampPromise=speak(vamp,wit.tipo,wit.nombre,wit.genero);
      const streamAbort=new AbortController();
      let fullWitText="";let hasObjTag=false;let msgAdded=false;
      const msgRef={current:null};
      const [adv]=await Promise.all([
        callClaude([{role:"user",content:`El abogado pregunto: "${text}"`}],buildAdversaryPrompt(caseData,userRole,difficulty))
          .then(result=>{if(!result.includes("SIN_OBJECION")&&!result.startsWith("CONDUCTA_IMPROPIA:"))streamAbort.abort();return result;}),
        callClaudeStreaming(
          [...hist.map(h=>({role:"user",content:h.q})),{role:"user",content:text}],
          buildWitnessPrompt(caseData,wit,phase,hist),
          (chunk,soFar)=>{
            if(streamAbort.signal.aborted)return;
            fullWitText=soFar;
            if(!msgAdded){msgRef.current=addMsg({role:"assistant",speaker:wit.tipo,content:"…",witnessName:wit.nombre});msgAdded=true;}
            const display=stripMd(soFar.replace(/\[OBJ:[^\]]+\]/gi,"").trim());
            if(msgRef.current&&display)updateMsg(msgRef.current._id,display);
            if(/\[OBJ:/i.test(soFar))hasObjTag=true;
          },1000,streamAbort.signal
        ).catch(e=>{if(e?.name!=="AbortError")throw e;}),
      ]);
      if(adv?.startsWith("CONDUCTA_IMPROPIA:")){
        streamAbort.abort();sabotageCount.current+=1;
        if(sabotageCount.current>=2){
          const termText=await callClaude([{role:"user",content:"Termina la audiencia por conducta impropia."}],buildJudgeTerminateSabotagePrompt());
          addMsg({role:"assistant",speaker:"juez",content:stripMd(termText)});await speakJudge(stripMd(termText));
          setLoading(false);setPhase("cerrado");setActive("none");setTrialClosed(true);return;
        }else{
          const warnText=await callClaude([{role:"user",content:"Llama la atención."}],buildJudgeWarningSabotagePrompt());
          setJudgeRuling(stripMd(warnText));addMsg({role:"assistant",speaker:"juez",content:stripMd(warnText)});
          await speakJudge(stripMd(warnText));setTimeout(()=>setJudgeRuling(null),12000);setLoading(false);return;
        }
      }
      if(adv&&!adv.includes("SIN_OBJECION")){
        stopSpeech();setSpeaking(false);setSpeakerName("");
        const advSpeaker=userRole==="fiscal"?"defensor":"fiscal";
        const cleanAdv=normalizeObjText(stripMd(adv));
        addMsg({role:"assistant",speaker:advSpeaker,content:cleanAdv,isObjection:true});await speak(cleanAdv,advSpeaker);
        const ruling=await callClaude(
          [{role:"user",content:"Resuelve la objeción de inmediato según el criterio del Código Orgánico Integral Penal y el contexto provisto."}],
          buildJudgeRulingPrompt({
            userRole,
            phase: phaseRef.current,
            isUserObjecting: false,                 // la IA objetó al usuario
            objectionType: extractObjType(adv),
            originalQuestion: text,
            witnessHistory: hist,
          })
        );
        const cleanRuling=stripMd(ruling).replace(/DECISION:|FUNDAMENTO:|INSTRUCCION:/g,"").trim();
        setJudgeRuling(cleanRuling);addMsg({role:"assistant",speaker:"juez",content:cleanRuling});
        await speakJudge(cleanRuling);setTimeout(()=>setJudgeRuling(null),10000);
        if(ruling.toLowerCase().includes("ha lugar")&&!ruling.toLowerCase().includes("no ha lugar")){setLoading(false);return;}
      }else{
        await vampPromise;
        const cleanFull=stripMd(fullWitText.replace(/\[OBJ:[^\]]+\]/gi,"").trim());
        if(hasObjTag){
          const objMatch=fullWitText.match(/\[OBJ:([^\]]+)\]/i);
          if(objMatch){
            const autoR=`Objecion de oficio. La pregunta resulta ${objMatch[1]}. Señor abogado, reformule.`;
            setJudgeRuling(autoR);addMsg({role:"assistant",speaker:"juez",content:autoR});
            await speakJudge(autoR);setTimeout(()=>setJudgeRuling(null),8000);setLoading(false);return;
          }
        }
        if(cleanFull&&!cancelled.current&&!streamAbort.signal.aborted)await speak(cleanFull,wit.tipo,wit.nombre,wit.genero);
        addWitnessHistory(idx,text,cleanFull);
      }
    }catch(e){addMsg({role:"assistant",speaker:"sistema",content:`Error: ${e?.message||"conexión fallida"}. Intenta de nuevo.`,isSystem:true});}
    setLoading(false);
  };
  sendMessageRef.current=sendMessage;

  const handleUserEndPhase=()=>{
    interruptRealtime();disconnectRealtime();setActive("none");
    handlePhaseTransition(witnessIdxRef.current,witnessQueue[witnessIdxRef.current],phaseRef.current);
  };

  const objectionResolveRef=useRef(null);
  const sabotageCount=useRef(0);

  const raiseObjection=async obj=>{
    setObjectionMode(false);
    interruptRealtime();
    stopSpeech();
    freezeTrial();
    stopListening();
    objectionPending.current=true;
    const speaker=userRole==="fiscal"?"fiscal":"defensor";
    addMsg({role:"user",content:`¡Objeción! ${obj.label} — ${obj.desc}`,isUserObj:true});
    await speakText(`Objeción. ${obj.label}.`,speaker,null,null);
    try{
      // Encontrar la pregunta más reciente del adversario IA para dar contexto al juez
      const lastAiQ = [...msgsRef.current].reverse().find(m => m.isAiQuestion)?.content || "";
      const ruling=await callClaude(
        [{role:"user",content:"Resuelve la objeción de inmediato según el criterio del Código Orgánico Integral Penal y el contexto provisto."}],
        buildJudgeRulingPrompt({
          userRole,
          phase: phaseRef.current,
          isUserObjecting: true,                // el usuario objetó a la IA
          objectionType: obj.label,
          originalQuestion: lastAiQ,
          witnessHistory: getWitnessHistory(witnessIdxRef.current),
        })
      );
      const cleanRuling=stripMd(ruling).replace(/DECISION:|FUNDAMENTO:|INSTRUCCION:/g,"").trim();
      const sustained=ruling.toLowerCase().includes("ha lugar")&&!ruling.toLowerCase().includes("no ha lugar");
      lastObjSustained.current=sustained;
      setJudgeRuling(cleanRuling);addMsg({role:"assistant",speaker:"juez",content:cleanRuling});
      await speakJudge(cleanRuling);
      setTimeout(()=>setJudgeRuling(null),10000);
      if(sustained)addMsg({role:"assistant",speaker:"juez",content:"El interrogador deberá reformular su pregunta.",isSystem:true});
    }catch{}
    objectionPending.current=false;
    unfreezeTrial();
    const wit=currentWitnessRef.current;
    if(wit&&!cancelled.current){
      const hist=getWitnessHistory(witnessIdxRef.current);
      const isAiTurn = activeRoleRef.current === "ai";
      connectRealtimeForUser(wit, currentPhaseRef.current, hist, isAiTurn);
      if(!isAiTurn) setTimeout(() => enableVAD(), 1500);
    }
  };

  const downloadReport=async(witness,wIdx)=>{
    const key=witness.tipo==="policia"?"policia":"perito";
    const pregen=cfg.expediente?.[key];
    if(pregen?.content){openDoc(generateReportHTML(witness,pregen.content,caseData));return;}
    setReportLoading(wIdx);
    try{const content=await callClaude([{role:"user",content:"Genera el informe ahora."}],buildReportPrompt(witness,caseData,cfg.crimeKey),1800);openDoc(generateReportHTML(witness,content,caseData));}
    catch{alert("Error al generar el informe.");}
    setReportLoading(null);
  };

  const handlePause=()=>{
    if(!isPaused){paused.current=true;stopSpeech();stopListening();setIsPaused(true);}
    else{paused.current=false;setIsPaused(false);}
  };
  const handleBack=()=>{cancelled.current=true;stopSpeech();flushJudgeQueue();destroyMic();disconnectRealtime();setSpeaking(false);onBack();};

  const fmt=s=>`${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
  const qCount=msgs.filter(m=>m.role==="user"&&!m.isObjection&&!m.isUserObj).length;
  const showTerminarInter  =!trialClosed&&trialPhase==="interrogatorio"      &&activeRole==="user"&&!loading;
  const showTerminarContrar=!trialClosed&&trialPhase==="contrainterrogatorio"&&activeRole==="user"&&!loading;
  const showEvaluar=trialClosed||qCount>=6;
  const phaseLabels={interrogatorio:"INTERROGATORIO",contrainterrogatorio:"CONTRAINTERROGATORIO",cerrado:"AUDIENCIA CERRADA"};
  const spLabels={juez:"JUEZ HABLANDO",secretaria:"SECRETARIA HABLANDO",testigo:"TESTIGO HABLANDO",perito:"PERITO HABLANDO",experto:"EXPERTO HABLANDO",policia:"AGENTE HABLANDO",fiscal:"FISCALIA HABLANDO",defensor:"DEFENSA HABLANDO"};
  const reportableWitnesses=witnessQueue.filter(w=>w.tipo==="perito"||w.tipo==="policia"||w.tipo==="experto");

  // ── Mapeo de roles a clases de color para las burbujas ──────────────────
  const roleBadge = (m) => {
    if (m.speaker === "secretaria") return { cls: "role-sec",  label: "Secretaría" };
    if (m.speaker === "juez")       return { cls: "role-juez", label: "Juez" };
    if (m.speaker === "fiscal")     return { cls: "role-fis",  label: `Fiscal${m.isAiQuestion ? " (IA)" : ""}` };
    if (m.speaker === "defensor")   return { cls: "role-def",  label: `Defensa${m.isAiQuestion ? " (IA)" : ""}` };
    if (m.role === "user") {
      const label = userRole === "fiscal" ? `Fiscal · ${userName || "Tú"}` : `Defensa · ${userName || "Tú"}`;
      return { cls: userRole === "fiscal" ? "role-fis" : "role-def", label };
    }
    // testigos por tipo
    const tw = WITNESS_TYPES[m.speaker];
    const testClsByType = { perito: "role-test1", policia: "role-test2", testigo: "role-test3", experto: "role-test4" };
    const cls = testClsByType[m.speaker] || "role-test3";
    const tipoLabel = tw?.label || "Testigo";
    return { cls, label: m.witnessName ? `${tipoLabel} · ${m.witnessName}` : tipoLabel };
  };

  const phaseBadgeClass = { interrogatorio: "badge-int", contrainterrogatorio: "badge-contra", cerrado: "badge-cerrado" };

  // Color del tag de temperamento (no depende de constants.js)
  const tempTagClass = {
    cooperativo: "tag-green", seguro: "tag-green",
    hostil: "tag-orange", evasivo: "tag-orange", desconfiado: "tag-orange",
    tecnico: "tag-blue", nervioso: "tag-blue", dramatico: "tag-blue",
  };

  return (
    <>
      <GlobalStyles />
      <style dangerouslySetInnerHTML={{ __html: TRIAL_CSS }} />
      <div className="litis-trial">

        {/* Overlay de orientación — solo aparece en teléfono vertical */}
        {!rotateSkipped && (
          <div className="rotate-overlay show">
            <img src="/litislab-logo-light.png" alt="LitisLab" className="rotate-logo" />
            <div className="rotate-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.48 2.52c3.27 1.55 5.61 4.72 5.97 8.48h1.5C23.44 4.84 18.29 0 12 0l-.66.03 3.81 3.81 1.33-1.32zm-6.25-.77c-.59-.59-1.54-.59-2.12 0L1.75 8.11c-.59.59-.59 1.54 0 2.12l12.02 12.02c.59.59 1.54.59 2.12 0l6.36-6.36c.59-.59.59-1.54 0-2.12L10.23 1.75zm4.6 19.44L2.81 9.17l6.36-6.36 12.02 12.02-6.36 6.36zM7.52 21.48C4.25 19.94 1.91 16.76 1.55 13H.05C.56 19.16 5.71 24 12 24l.66-.03-3.81-3.81-1.33 1.32z"/>
              </svg>
            </div>
            <h2>Gira tu teléfono a horizontal</h2>
            <p>La sala de juicio está diseñada para una vista amplia. En horizontal vas a ver mejor las burbujas, los testigos y los controles.</p>
            <button className="rotate-skip" onClick={() => setRotateSkipped(true)}>Continuar en vertical</button>
          </div>
        )}

        {/* ── TOPBAR ── */}
        <div className="trial-topbar">
          <button className="topbar-brand" onClick={handleBack} title="Volver">
            <img src="/litislab-logo-light.png" alt="LitisLab" />
          </button>
          <div className="topbar-case">
            <div className="topbar-case-title">{caseData?.titulo || "Audiencia"}</div>
            <div className="topbar-case-sub">{caseData?.fecha ? `${caseData.fecha} · ` : ""}{caseData?.articulo || ""}</div>
          </div>
          <div className="topbar-center">
            <span className={`topbar-badge ${phaseBadgeClass[trialPhase] || "badge-int"}`}>
              {phaseLabels[trialPhase] || ""}
            </span>
            {currentWitness && !trialClosed && (
              <>
                <span className="topbar-progress">{witnessIdx + 1}/{witnessQueue.length}</span>
                <span className="topbar-witness">{currentWitness.nombre.split(" ")[0]}</span>
              </>
            )}
            {(rtConnected || rtConnecting) && (
              <span className="topbar-rt">{rtConnecting ? "⟳ CONECTANDO" : "⚡ REALTIME"}</span>
            )}
          </div>
          <div className="topbar-timer">{fmt(elapsed)}</div>
          <div className="topbar-actions">
            {showTerminarInter && <button className="tbtn tbtn-solid" onClick={handleUserEndPhase}>Terminar interrogatorio</button>}
            {showTerminarContrar && <button className="tbtn tbtn-solid" onClick={handleUserEndPhase}>Terminar contrainterrogatorio</button>}
            {!trialClosed && <button className="tbtn tbtn-solid" onClick={handlePause}>{isPaused ? "▶ Reanudar" : "⏸ Pausar"}</button>}
            {!trialClosed && <button className="tbtn tbtn-danger" onClick={handleCloseTrial}>Terminar juicio</button>}
            {showEvaluar && <button className="tbtn tbtn-accent" onClick={() => onEval(msgsRef.current)}>Ver evaluación</button>}
            <button className="tbtn tbtn-ghost" onClick={handleBack}>Salir</button>
          </div>
        </div>

        {/* ── PAUSA OVERLAY ── */}
        {isPaused && (
          <div className="trial-pause-overlay">
            <div className="pause-label">Audiencia en pausa</div>
            <img src="/litislab-logo-light.png" alt="LitisLab" className="pause-logo" />
            <p className="pause-text">Presiona Reanudar para continuar.</p>
            <button className="pause-btn" onClick={handlePause}>▶ Reanudar audiencia</button>
          </div>
        )}

        {/* ── BODY ── */}
        <div className="trial-body">

          {/* SIDEBAR */}
          <div className="trial-sidebar">
            {currentWitness && (
              <div className="sidebar-section">
                <div className="sidebar-section-label">En estrado</div>
                <div className="active-witness-card">
                  <div className="aw-name">{currentWitness.nombre}</div>
                  <div className="aw-role">{WITNESS_TYPES[currentWitness.tipo]?.label || "Testigo"}</div>
                  <span className={`aw-tag ${tempTagClass[currentWitness.temperamento] || "tag-green"}`}>
                    {(PERSONAS[currentWitness.temperamento]?.label || "Cooperativo")}
                  </span>
                </div>
              </div>
            )}

            <div className="sidebar-section">
              <div className="sidebar-section-label">Testigos</div>
              <div className="witness-list">
                {witnessQueue.map((w, i) => {
                  const done = i < witnessIdx;
                  const active = i === witnessIdx;
                  const dotColor = done ? "var(--green)" : active ? "var(--accent)" : "var(--light)";
                  return (
                    <div key={i} className={`wl-item ${active ? "active" : ""}`} style={{ opacity: done ? 0.5 : 1 }}>
                      <span className="wl-dot" style={{ background: dotColor }} />
                      <div>
                        <div className="wl-name">{w.nombre}</div>
                        <div className="wl-role">{WITNESS_TYPES[w.tipo]?.label || "Testigo"}{active ? " · en estrado" : ""}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {reportableWitnesses.length > 0 && (
              <div className="sidebar-section">
                <div className="sidebar-section-label">Informes</div>
                <div className="doc-list">
                  {reportableWitnesses.map((w, i) => {
                    const wIdx = witnessQueue.indexOf(w);
                    const isPolicial = w.tipo === "policia";
                    return (
                      <button key={i} className="doc-item" onClick={() => downloadReport(w, wIdx)} disabled={reportLoading === wIdx}>
                        <div className={`doc-icon ${isPolicial ? "doc-icon-green" : "doc-icon-blue"}`}>
                          {reportLoading === wIdx ? <span className="doc-spinner" /> : (isPolicial ? "📋" : "🔬")}
                        </div>
                        <div>
                          <div className="doc-item-name">{w.nombre.split(" ")[0]}</div>
                          <div className="doc-item-sub">{isPolicial ? "Parte Policial" : "Informe Pericial"}</div>
                        </div>
                        <span className="doc-open">ABRIR →</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="sidebar-section">
              <div className="sidebar-section-label">Teorías</div>
              <div className="theory-mini">
                <div className="theory-mini-label" style={{ color: "var(--blue)" }}>Fiscalía</div>
                <div className="theory-mini-text">{caseData?.teoria_fiscal}</div>
              </div>
              <div className="theory-mini">
                <div className="theory-mini-label" style={{ color: "var(--green)" }}>Defensa</div>
                <div className="theory-mini-text">{caseData?.teoria_defensa}</div>
              </div>
            </div>

            <div className="sidebar-section">
              <div className="sidebar-section-label">Sesión</div>
              <div className="session-stat">
                <span className="session-stat-label">Preguntas</span>
                <span className="session-stat-val">{qCount}</span>
              </div>
              <div className="session-stat">
                <span className="session-stat-label">Objeciones</span>
                <span className="session-stat-val">{msgs.filter(m => m.isObjection || m.isUserObj).length}</span>
              </div>
            </div>
          </div>

          {/* MAIN */}
          <div className="trial-main">

            {/* Banner ruling del juez */}
            {judgeRuling && (
              <div className="trial-ruling-bar">
                <span>⚖</span>
                <span className="trial-ruling-text">{judgeRuling}</span>
              </div>
            )}

            {/* Banner IA interrogando */}
            {activeRole === "ai" && !trialClosed && (
              <div className="trial-ai-bar">
                <div className="trial-ai-dot" />
                <span>{userRole === "fiscal" ? "LA DEFENSA (IA)" : "LA FISCALÍA (IA)"} ESTÁ INTERROGANDO — PUEDES OBJETAR</span>
              </div>
            )}

            {/* Hint bar */}
            {currentTip && !judgeRuling && activeRole !== "ai" && (
              <div className="hint-bar">
                <span>💡</span>
                <span className="hint-bar-text">{currentTip}</span>
              </div>
            )}

            {/* Transcript */}
            <div className="transcript-area">
              {msgs.length === 0 ? (
                <div className="transcript-empty">
                  <p>La sala aguarda en silencio.</p>
                </div>
              ) : (
                msgs.map((m, i) => {
                  if (m.isSystem) {
                    return (
                      <div key={m._id || i} className="msg sys-line">
                        <span>{m.content}</span>
                      </div>
                    );
                  }
                  const badge = roleBadge(m);
                  const isSecretary = m.speaker === "secretaria";
                  return (
                    <div key={m._id || i} className={`msg ${isSecretary ? "sec-msg" : ""} ${m.isObjection ? "obj-msg" : ""}`}>
                      <div className="msg-header">
                        <span className={`msg-role-badge ${badge.cls}`}>{badge.label}</span>
                        {m.isObjection && <span className="msg-obj-tag">¡OBJECIÓN!</span>}
                      </div>
                      <div className="msg-text" dangerouslySetInnerHTML={{ __html: renderMsgContent(m.content) }} />
                    </div>
                  );
                })
              )}
              {loading && (
                <div className="processing">
                  <div className="processing-dots"><span /><span /><span /></div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Indicador de voz reproduciéndose */}
            {speaking && (
              <div className="speaking-bar">
                {[0.5, 0.8, 1.1, 0.8, 0.5].map((d, i) => (
                  <div key={i} className="wave-bar" style={{ animationDuration: `${d}s`, animationDelay: `${i * 0.1}s` }} />
                ))}
                <span className="speaking-label">{spLabels[speakerName] || "REPRODUCIENDO"}</span>
              </div>
            )}

            {/* Estado del micrófono / realtime cuando es turno del usuario */}
            {activeRole === "user" && !trialClosed && (
              <div className={`mic-status ${rtConnected ? "mic-rt" : rtConnecting ? "mic-connecting" : rtError ? "mic-error" : micListening ? "mic-listening" : ""}`}>
                {rtConnected ? (
                  <>
                    <div className="mic-waves">
                      {[0.4, 0.7, 1.0, 0.7, 0.4].map((d, i) => (
                        <div key={i} className="wave-bar wave-blue" style={{ animationDuration: `${d}s`, animationDelay: `${i * 0.12}s` }} />
                      ))}
                    </div>
                    <span className="mic-status-text rt">⚡ REALTIME ACTIVO — HABLA AHORA</span>
                    <span className="mic-status-hint">Di "no tengo más preguntas" para terminar</span>
                  </>
                ) : rtConnecting ? (
                  <>
                    <span className="mic-spinner" />
                    <span className="mic-status-text rt">Conectando Realtime — espera un momento...</span>
                  </>
                ) : rtError ? (
                  <span className="mic-status-text err">⚠ Realtime: {rtError}</span>
                ) : micListening && !micError ? (
                  <>
                    <div className="mic-waves">
                      {[0.4, 0.7, 1.0, 0.7, 0.4].map((d, i) => (
                        <div key={i} className="wave-bar wave-green" style={{ animationDuration: `${d}s`, animationDelay: `${i * 0.12}s` }} />
                      ))}
                    </div>
                    <span className="mic-status-text grn">
                      ESCUCHANDO
                      {lastTranscript && <span className="mic-transcript"> "{lastTranscript}"</span>}
                    </span>
                    <span className="mic-status-hint">Di "no tengo más preguntas" para terminar</span>
                  </>
                ) : micError ? (
                  <span className="mic-status-text err">⚠ {typeof micError === "string" ? micError : "Micrófono no disponible"} — usa el campo de texto</span>
                ) : (
                  <span className="mic-status-text">Iniciando...</span>
                )}
              </div>
            )}

            {/* Menú de objeción */}
            {objectionMode && (
              <div className="objection-menu">
                <span className="objection-menu-label">TIPO:</span>
                {OBJECTION_TYPES.map(o => (
                  <button key={o.key} className="objection-chip" onClick={() => raiseObjection(o)}>{o.label}</button>
                ))}
                <button className="objection-close" onClick={() => setObjectionMode(false)}>✕</button>
              </div>
            )}

            {/* Input bar */}
            <div className="input-bar">
              <button
                className={`input-mic ${objectionMode ? "mic-obj-active" : ""}`}
                onClick={() => setObjectionMode(v => !v)}
                title="Levantar objeción"
                disabled={trialClosed || (activeRole !== "user" && activeRole !== "ai")}
              >
                🔨
              </button>
              <input
                className="input-field"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage(input)}
                onFocus={() => tutorialStep === 1 && setTutorialStep(2)}
                disabled={loading || activeRole !== "user" || trialClosed}
                placeholder={
                  trialClosed ? "Audiencia cerrada"
                  : activeRole !== "user" ? "La contraparte interroga..."
                  : rtConnected ? "Habla por el micrófono (Realtime activo)..."
                  : rtConnecting ? "Espera, conectando Realtime..."
                  : rtError ? "Realtime falló — escribe aquí"
                  : micListening ? "O escribe aquí..."
                  : "Escribe tu pregunta o argumento..."
                }
              />
              <button
                className="input-send"
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || loading || activeRole !== "user" || trialClosed}
              >
                Enviar →
              </button>
            </div>
          </div>
        </div>

        <TutorialTooltip step={tutorialStep} total={TUTORIAL_STEPS.length} onNext={() => setTutorialStep(s => s + 1 >= TUTORIAL_STEPS.length ? -1 : s + 1)} />
      </div>
    </>
  );
}
